import {
    IonAvatar,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSearchbar,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    peopleOutline,
    personOutline,
    refreshOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    PlayersById,
    TeamDetail,
    getPlayerDetail,
    getPlayers,
    getTeamDetail
} from "../services/mexfbaApi";

import "./RosterTeam.css";

interface RouteParams {
    id: string;
}

type PlayerRecord = Record<string, unknown>;

const POSITION_LABELS: Record<string, string> = {
    "1": "QB",
    "2": "RB",
    "3": "WR",
    "4": "DL",
    "5": "LB",
    "6": "DB",
    "7": "K",
    "8": "OL"
};

const POSITION_FILTERS = [
    "Todos",
    "QB",
    "RB",
    "WR",
    "DL",
    "LB",
    "DB",
    "K",
    "OL"
];

function getPlayerRecord(player: unknown): PlayerRecord {
    return player as PlayerRecord;
}

function getPositionIndex(player: unknown): string {
    const record = getPlayerRecord(player);

    return String(
        record.pos ??
            record.posicion ??
            ""
    ).trim();
}

function getPosition(player: unknown): string {
    return POSITION_LABELS[getPositionIndex(player)] || "—";
}

function getPlayerName(player: unknown): string {
    const record = getPlayerRecord(player);

    return String(record.nombre || "Jugador sin nombre");
}

function getPlayerNumber(player: unknown): string {
    const record = getPlayerRecord(player);

    return String(record.numero || "—");
}

function getPlayerTeamId(player: unknown): string {
    const record = getPlayerRecord(player);

    return String(record.equipo_id || "");
}

function getPlayerCompetitionId(player: unknown): string {
    const record = getPlayerRecord(player);

    return String(record.comp_id || "");
}

function getPlayerImage(player: unknown): string {
    const record = getPlayerRecord(player);

    return String(record.img || "");
}

function getInitial(player: unknown): string {
    return getPlayerName(player).charAt(0).toUpperCase();
}
function getImageUrl(image: string): string {
    if (!image) {
        return "";
    }

    if (
        image.startsWith("http://") ||
        image.startsWith("https://") ||
        image.startsWith("data:")
    ) {
        return image;
    }

    const normalizedImage = image.replace(
        /^(\.\.\/)+/,
        ""
    );

    return `${process.env.PUBLIC_URL || ""}/${normalizedImage}`;
}

const TeamRoster: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [teamDetail, setTeamDetail] =
        useState<TeamDetail | null>(null);
    const [players, setPlayers] = useState<PlayersById>({});
    const [playerImages, setPlayerImages] = useState<
    Record<string, string>
>({});
    const [searchText, setSearchText] = useState("");
    const [activePosition, setActivePosition] = useState("Todos");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadRoster = useCallback(
    async function (forceRefresh = false) {
        try {
            setError("");

            if (forceRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            const [detail, playersData] = await Promise.all([
                getTeamDetail(id, forceRefresh),
                getPlayers(forceRefresh)
            ]);

            const competitionId = String(
                detail.Info.comp_id || ""
            );

            const rosterPlayers = Object.entries(playersData)
                .filter(function ([, player]) {
                    return getPlayerTeamId(player) === id;
                })
                .filter(function ([, player]) {
                    const playerCompetition =
                        getPlayerCompetitionId(player);

                    return (
                        !competitionId ||
                        !playerCompetition ||
                        playerCompetition === competitionId
                    );
                });

            const playerDetailResults = await Promise.all(
                rosterPlayers.map(async function ([playerId]) {
                    try {
                        const playerDetail = await getPlayerDetail(
                            playerId,
                            forceRefresh
                        );

                        return {
                            id: playerId,
                            image: getImageUrl(
                                String(playerDetail.Info.img || "")
                            )
                        };
                    } catch (playerError) {
                        console.warn(
                            `No fue posible cargar la imagen del jugador ${playerId}.`,
                            playerError
                        );

                        return {
                            id: playerId,
                            image: ""
                        };
                    }
                })
            );

            const imagesByPlayerId: Record<string, string> = {};

            playerDetailResults.forEach(function (result) {
                if (result.image) {
                    imagesByPlayerId[result.id] = result.image;
                }
            });

            setTeamDetail(detail);
            setPlayers(playersData);
            setPlayerImages(imagesByPlayerId);
        } catch (requestError) {
            console.error(
                "No fue posible cargar el plantel:",
                requestError
            );

            setError(
                "No fue posible cargar los jugadores de este equipo."
            );
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    },
    [id]
);


    useEffect(
        function () {
            loadRoster();
        },
        [loadRoster]
    );

    const roster = useMemo(
        function () {
            const competitionId = String(
                teamDetail?.Info.comp_id || ""
            );

            return Object.entries(players)
                .map(function ([playerId, player]) {
                    return {
                        id: playerId,
                        data: player
                    };
                })
                .filter(function (player) {
                    return getPlayerTeamId(player.data) === id;
                })
                .filter(function (player) {
                    const playerCompetition =
                        getPlayerCompetitionId(player.data);

                    return (
                        !competitionId ||
                        !playerCompetition ||
                        playerCompetition === competitionId
                    );
                })
                .filter(function (player) {
                    const matchesPosition =
                        activePosition === "Todos" ||
                        getPosition(player.data) === activePosition;

                    const matchesSearch = getPlayerName(player.data)
                        .toLocaleLowerCase("es-MX")
                        .includes(
                            searchText.toLocaleLowerCase("es-MX").trim()
                        );

                    return matchesPosition && matchesSearch;
                })
                .sort(function (firstPlayer, secondPlayer) {
                    const firstPosition = Number(
                        getPositionIndex(firstPlayer.data)
                    );
                    const secondPosition = Number(
                        getPositionIndex(secondPlayer.data)
                    );

                    if (firstPosition !== secondPosition) {
                        return firstPosition - secondPosition;
                    }

                    return getPlayerName(firstPlayer.data).localeCompare(
                        getPlayerName(secondPlayer.data),
                        "es-MX"
                    );
                });
        },
        [activePosition, id, players, searchText, teamDetail]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadRoster(true);
        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Plantel</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="roster-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !teamDetail) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Plantel</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="roster-content">
                    <div className="roster-empty">
                        <IonIcon icon={peopleOutline} />
                        <strong>No fue posible abrir el plantel</strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadRoster(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const info = teamDetail.Info;

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="roster-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar al equipo"
                            onClick={function () {
                                history.push(`/team/${id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Plantel</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar plantel"
                            disabled={refreshing}
                            onClick={function () {
                                loadRoster(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="roster-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando plantel..."
                    />
                </IonRefresher>

                <section
                    className="roster-hero"
                    style={
                        info.color
                            ? {
                                  background: `linear-gradient(135deg, ${info.color}, #173e70)`
                              }
                            : undefined
                    }
                >
                    {info.logo ? (
                        <img
                            src={info.logo}
                            alt={`Logo de ${info.nombre}`}
                        />
                    ) : (
                        <div className="roster-hero__placeholder">
                            {info.nombre?.charAt(0) || "E"}
                        </div>
                    )}

                    <div>
                        <span>PLANTEL</span>
                        <h1>{info.nombre}</h1>
                        <p>{info.comp_nombre || "Temporada actual"}</p>
                    </div>
                </section>

                <section className="roster-navigation">
                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/team/${id}`);
                        }}
                    >
                        Calendario
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/table/${id}`);
                        }}
                    >
                        Tabla
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/statsTeam/${id}`);
                        }}
                    >
                        Jugadores
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/teamStatsTeam/${id}`);
                        }}
                    >
                        Equipo
                    </IonButton>

                    <IonButton>Plantel</IonButton>
                </section>

                <section className="roster-section">
                    <div className="roster-heading">
                        <div>
                            <span>JUGADORES REGISTRADOS</span>
                            <h2>{roster.length} disponibles</h2>
                        </div>

                        <IonIcon icon={peopleOutline} />
                    </div>

                    <IonSearchbar
                        className="roster-search"
                        value={searchText}
                        debounce={220}
                        placeholder="Buscar jugador"
                        onIonChange={function (event) {
                            setSearchText(event.detail.value || "");
                        }}
                    />

                    <div className="roster-filters">
                        {POSITION_FILTERS.map(function (position) {
                            return (
                                <IonButton
                                    key={position}
                                    fill={
                                        activePosition === position
                                            ? "solid"
                                            : "outline"
                                    }
                                    onClick={function () {
                                        setActivePosition(position);
                                    }}
                                >
                                    {position}
                                </IonButton>
                            );
                        })}
                    </div>

                    {roster.length === 0 ? (
                        <div className="roster-no-results">
                            <IonIcon icon={personOutline} />
                            <strong>No encontramos jugadores</strong>
                            <span>
                                Ajusta la búsqueda o selecciona otra posición.
                            </span>
                        </div>
                    ) : (
                        <div className="roster-list">
                            {roster.map(function (player) {
    const image =
        playerImages[player.id] ||
        getImageUrl(getPlayerImage(player.data));


                                return (
                                    <button
                                        className="roster-player"
                                        key={player.id}
                                        type="button"
                                        onClick={function () {
                                            history.push(
                                                `/player/${player.id}`
                                            );
                                        }}
                                    >
                                        <IonAvatar>
                                            {image ? (
                                                <img
                                                    src={image}
                                                    alt={`Foto de ${getPlayerName(
                                                        player.data
                                                    )}`}
                                                />
                                            ) : (
                                                <span>
                                                    {getInitial(player.data)}
                                                </span>
                                            )}
                                        </IonAvatar>

                                        <div className="roster-player__info">
                                            <strong>
                                                {getPlayerName(player.data)}
                                            </strong>

                                            <span>
                                                {getPosition(player.data)}
                                            </span>
                                        </div>

                                        <div className="roster-player__number">
                                            #{getPlayerNumber(player.data)}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </section>
            </IonContent>
        </IonPage>
    );
};

export default TeamRoster;
